import { Form, Tooltip } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Styles } from "./style";
import { AppSelectionList } from "types/app.type";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import Button from "@lhd/components/Button";
import { COLUMN_WIDTH } from "@lhd/features/Apps/AppForm/constants";
import Title from "@lhd/components/CloneApp/components/Title";
import TableDiv, { Column } from "@lhd/components/TableDiv";
import CellRender from "./CellRender";
import { uniqueId } from "lodash";
import { getDefaultValueByAppPart } from "@lhd/components/CloneApp/untils";
import { IPermissionItem } from "@lhd/types";

/* ======================= */

interface IProps {
  item: AppPartType;
  selectionDataList: AppSelectionList;
  isPDF?: boolean;
  isDisabled?: boolean;
  permission?: IPermissionItem;
  onChange?: () => void;
}

type ObjectType = {
  [key: string]: any;
};

/* ======================= */
/* Sortable Row            */
/* ======================= */

const SortableRow = ({
  id,
  disabled,
  children,
}: {
  id: string;
  disabled?: boolean;
  children: (dragHandleProps: any) => React.ReactNode;
}) => {
  const preStyleRef = useRef<{
    transform: string | undefined;
    transition: string | undefined;
  }>();
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
    over,
    ...v
  } = useSortable({ id, disabled });

  const style =
    !transform && isDragging
      ? preStyleRef.current
      : {
          transform: CSS.Translate.toString({
            x: 0,
            y: transform?.y || 0,
            scaleX: 1,
            scaleY: 1,
          }),
          transition,
        };

  useEffect(() => {
    preStyleRef.current = style;
  }, [style]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDragging ? "is-dragging" : ""}
      {...attributes}
    >
      {children(listeners)}
    </div>
  );
};

const isRowEmpty = (record: any, children?: AppPartType[]) => {
  if (!children || children.length === 0) return true;
  return children.every((child) => {
    const val = record[String(child.id)];
    return val === undefined || val === null || val === "";
  });
};

/* ======================= */
/* Main Component          */
/* ======================= */

const TableCustom = ({
  item,
  selectionDataList,
  isPDF,
  isDisabled,
  permission,
  onChange,
}: IProps) => {
  const [versionRender, setVersionRender] = useState(uniqueId());
  const [isCollapse, setIsCollapse] = useState(false);
  const form = Form.useFormInstance();
  const tableId = item.id?.toString() || "";
  const tablePath = ["tables", tableId];
  const tablePathValue = ["tables", tableId, "value"];
  const sensors = useSensors(useSensor(PointerSensor));

  /* =======================
     Helpers
  ======================= */

  const createNamePath = (client_id: string, app_part_id: string | number) => [
    ...tablePathValue,
    client_id,
    String(app_part_id),
  ];

  const createNewEmptyRow = () => {
    const row: any = { client_id: Date.now().toString() };
    item.children?.forEach((appPart) => {
      if (appPart.id) row[appPart.id] = getDefaultValueByAppPart(appPart);
    });
    return row;
  };

  /* =======================
     DataSource
  ======================= */
  const tableValueWatched = Form.useWatch(tablePathValue, form) || {};

  const dataSource = useMemo(() => {
    const data = Object.entries(tableValueWatched).map(
      ([client_id, value]: any) => ({
        ...value,
        client_id,
      }),
    );
    return [...data].sort((a, b) => a.row_order - b.row_order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionRender, tableValueWatched]);

  /* =======================
     Columns
  ======================= */
  const sortedChildren = useMemo(
    () =>
      [...(item.children || [])].sort(
        (a, b) => a.item_config_data.x - b.item_config_data.x,
      ),
    [item.children],
  );

  const columns = useMemo(() => {
    return sortedChildren.map((child, indexAppPart) => {
      const label = child.label_text || child.name || "";
      let width = child.item_config_data.w * COLUMN_WIDTH + 2;
      if (label === "章条項" || label === "章条项") {
        width = Math.round((width * 2) / 3);
      }

      return {
        key: child.id?.toString() || "",
        title: <Title item={child} />,
        dataIndex: child.id?.toString(),
        width,
        render: (_: any, record: ObjectType, index: number) => {
          return (
            <div style={{ width: "100%" }}>
              {indexAppPart === 0 && (
                <>
                  <Form.Item
                    hidden
                    name={createNamePath(record.client_id, "info")}
                  />
                  <Form.Item
                    hidden
                    name={createNamePath(record.client_id, "row_order")}
                    initialValue={index}
                  />
                </>
              )}
              <CellRender
                selectionDataList={selectionDataList}
                item={child}
                isDisabled={isDisabled}
                index={index}
                option={{
                  namePath: createNamePath(
                    record.client_id,
                    child.id?.toString() || "",
                  ),
                  isTable: true,
                  isDisabled: isDisabled,
                  onChange: onChange,
                }}
              />
            </div>
          );
        },
      };
    });
  }, [sortedChildren, selectionDataList, isDisabled]);

  const pdfGridTemplateColumns = useMemo(() => {
    if (!isPDF || columns.length === 0) return undefined;

    let pdfColumnWeights = [1, 1, 2, 1, 1, 8];
    const isAgreements = window.location.pathname.includes("agreements");
    if (isAgreements && columns.length === 5) {
      pdfColumnWeights = [1, 3, 1, 1, 8];
    }

    return columns
      .map((_, index) => {
        const weight = pdfColumnWeights[index] ?? 0.8;
        return `${weight}fr`;
      })
      .join(" ");
  }, [isPDF, columns]);

  /* =======================
     CRUD
  ======================= */

  const handleAddRow = (index?: number) => {
    const tableValue = form.getFieldValue(tablePathValue) || {};
    const entries = Object.entries(tableValue);

    const newRow = createNewEmptyRow();
    entries.splice(index ?? entries.length, 0, [newRow.client_id, newRow]);

    form.setFieldValue(tablePathValue, Object.fromEntries(entries));
    setVersionRender(uniqueId());
  };

  const handleRemoveRow = (index: number) => {
    if (permission?.delete === false) return;
    const tableValue = form.getFieldValue(tablePathValue) || {};
    const entries = Object.entries(tableValue);
    const [removed] = entries.splice(index, 1) as any;
    if (removed && removed[1]?.info?.isOld) {
      const dataPre = form.getFieldValue([...tablePath, "_destroy"]);
      const dataDelete = [
        ...dataPre,
        { row_id: removed[1]?.info.row_id, _destroy: true },
      ];
      form.setFieldValue([...tablePath, "_destroy"], dataDelete);
    }

    form.setFieldValue(tablePathValue, Object.fromEntries(entries));

    setVersionRender(uniqueId());
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const tableValue = form.getFieldValue(tablePathValue) || {};
    const entries = Object.entries(tableValue);

    const oldIndex = entries.findIndex(([id]) => id === active.id);
    const newIndex = entries.findIndex(([id]) => id === over.id);

    const newEntries = arrayMove(entries, oldIndex, newIndex);
    const newEntriesAddRowOder = newEntries.map(([clientId, record], index) => {
      return [clientId, { ...(record as any), row_order: index }];
    });
    form.setFieldValue(
      tablePathValue,
      Object.fromEntries(newEntriesAddRowOder),
    );
    setVersionRender(uniqueId());
  };

  /* =======================
     Action Column
  ======================= */

  const actionCell = {
    key: "__action__",
    width: 80,
    className: "action_cell",
    fixed: "left",
    title: (
      <Tooltip title={isCollapse ? "表示" : "非表示"}>
        <Button
          className="button-collapse"
          onClick={() => setIsCollapse(!isCollapse)}
          label={isCollapse ? "表示" : "非表示"}
        />
      </Tooltip>
    ),
    render: (_: any, __: any, index: number, dragHandleProps: any) =>
      !isDisabled && (
        <div className="action-buttons-container">
          <div
            className={`button sort-button ${
              permission?.edit === false ? "disabled" : ""
            }`}
            {...dragHandleProps}
          >
            並替
          </div>

          <div
            className={`button remove-button ${
              permission?.delete === false ? "disabled" : ""
            }`}
            onClick={() => handleRemoveRow(index)}
          >
            削除
          </div>

          {index > 0 && (
            <div
              className={`button insert-button top  ${
                permission?.edit === false ? "disabled" : ""
              }`}
              onClick={() => handleAddRow(index)}
            >
              挿入
            </div>
          )}
          {index + 1 === dataSource.length && (
            <div
              className={`button insert-button bottom ${
                permission?.edit === false ? "disabled" : ""
              }`}
              onClick={() => handleAddRow(index + 1)}
            >
              挿入
            </div>
          )}
        </div>
      ),
  } as Column<any>;

  const sortableItems = useMemo(
    () => dataSource.map((record) => record.client_id),
    [dataSource],
  );

  const filteredDataSource = useMemo(() => {
    return dataSource;
  }, [dataSource]);

  useEffect(() => {
    if (!dataSource?.length) handleAddRow(0);
  }, [dataSource.length]);

  /* =======================
     Render
  ======================= */
  return (
    <Styles
      className={`${isDisabled ? "mode_view" : ""} ${isPDF ? "pdf-mode" : ""} ${
        isDisabled ? "disabled" : ""
      }`}
    >
      <Form.Item name={[...tablePath, "_destroy"]} hidden initialValue={[]} />
      <Form.Item name={[...tablePathValue]} hidden />
      <DndContext
        sensors={useSensors(useSensor(PointerSensor))}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortableItems}
          strategy={verticalListSortingStrategy}
        >
          <TableDiv
            isCollapse={isCollapse}
            isPDF={isPDF}
            pdfGridTemplateColumns={pdfGridTemplateColumns}
            columns={isPDF ? [...columns] : [actionCell, ...columns]}
            dataSource={filteredDataSource}
            rowKey={(r) => r.client_id}
            renderRow={(record, index, row) => (
              <SortableRow id={record.client_id} disabled={isDisabled}>
                {(dragHandleProps) => row(dragHandleProps)}
              </SortableRow>
            )}
          />
        </SortableContext>
      </DndContext>
    </Styles>
  );
};

export default TableCustom;
